import {
  Attachment,
  BaseDesignElement,
  LinkedElement,
  DesignElement,
  ElementList,
  Policy,
  Product,
  ProductWithoutId,
  PresignedUrlResponse,
  QualioUser,
  ReviewList,
  TestResult,
  ReviewRecord,
  ReleaseRecord,
  ReleaseReport,
  Approval,
  TraceabilityMatrix,
  ReviewHistory,
  DesignElementVersion,
  isDesignElementTestCase,
  isDesignElementFMEARisk,
  FMEARiskDesignElement,
} from "@design-controls/types";
import axios, { AxiosInstance } from "axios";
import { getDesignControlsBaseURL } from "./apiConfig";

let api: AxiosInstance | null = null;

function getApi(): AxiosInstance {
  if (!api) {
    api = axios.create({
      headers: {
        "Content-Type": "application/json",
      },
      baseURL: getDesignControlsBaseURL(),
      withCredentials: true,
    });
  }
  return api;
}

export const designControlsApi = {
  async createProduct(company: number, product: ProductWithoutId) {
    const response = await getApi().post<{ data: Product }>(
      `/company/${company}/product-development/product`,
      { data: product },
    );

    return Product.parse({
      ...response.data.data,
      // handle type issue
      type:
        (response.data.data.type as any) === ""
          ? undefined
          : response.data.data.type,
    });
  },
  async retrieveProductById(
    company: number,
    product: string,
  ): Promise<Product> {
    const allProducts = await this.retrieveProductsByCompany(company);
    const selected = allProducts.find((p) => p.id === product);

    if (selected) {
      return selected;
    }

    throw new Error("Product not found");
  },
  async retrieveProductsByCompany(company: number): Promise<Product[]> {
    return getApi()
      .get(`/company/${company}/product-development/product`)
      .then((res) =>
        Product.array().parse(
          res.data.data.map((product: any) => ({
            ...product,
            type: product.type === "" ? undefined : product.type,
          })),
        ),
      )
      .catch((e) => {
        console.error(e);
        return [];
      });
  },
  async updateProduct(company: number, product: Product) {
    await getApi().put(
      `/company/${company}/product-development/product/${product.id}`,
      { data: product },
    );
  },
  async deleteProduct(company: number, product: string): Promise<boolean> {
    const response = await getApi().delete(
      `/company/${company}/product-development/product/${product}`,
    );
    return response.status === 204;
  },
  async createPolicy(company: number, product: string, policy: Policy) {
    await getApi().post(
      `/company/${company}/product-development/product/${product}/config`,
      {
        data: {
          ...policy,
          codeStrategy: ["GENERATED", "SOURCE"].includes(policy.codeStrategy)
            ? policy.codeStrategy
            : "GENERATED",
          workflow: { states: [] },
        },
      },
    );
  },
  async updatePolicy(company: number, product: string, policy: Policy) {
    return getApi().put(
      `/company/${company}/product-development/product/${product}/config/${policy.type}`,
      { data: { ...policy, workflow: { states: [] } } },
    );
  },
  retrieveAllPolicies(company: number, product: string): Promise<Policy[]> {
    return getApi()
      .get(`/company/${company}/product-development/product/${product}/config`)
      .then((res) => Policy.array().parse(res.data.data));
  },
  deletePolicy(company: number, product: string, policy: Policy["type"]) {
    return getApi()
      .delete(
        `/company/${company}/product-development/product/${product}/config/${policy}`,
      )
      .then(() => undefined);
  },
  retrieveArchivePrefixes(
    company: number,
    product: string,
  ): Promise<Array<string>> {
    return getApi()
      .get<{
        data: Array<string>;
      }>(
        `/company/${company}/product-development/product/${product}/archivePrefixes`,
      )
      .then((res) => res.data.data);
  },
  async query(
    company: number,
    product: string,
    type?: string,
  ): Promise<Array<ElementList>> {
    const elementTypeQuery = type ? `?type=${type}` : "";

    const response = await getApi().get<{ data: Array<ElementList> }>(
      `/company/${company}/product-development/product/${product}/openIssue${elementTypeQuery}`,
    );

    return ElementList.array()
      .parse(response.data.data)
      .map((item) => {
        // If result is missing from testResult we drop the entire object from the testcase
        if ("testResult" in item && item.testResult?.result === undefined) {
          const { testResult, ...rest } = item;
          return rest;
        }
        return item;
      })
      .sort(
        (a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime(),
      );
  },
  async getById(
    company: number,
    product: string,
    qualityItemId: string,
    qualityItemVersion?: string,
  ) {
    let url = `/company/${company}/product-development/product/${product}/qualityItem/${qualityItemId}`;
    if (qualityItemVersion !== undefined) {
      url = `${url}/version/${qualityItemVersion}`;
    }

    const response = await getApi().get<{ data: DesignElement }>(url);

    let qualityItem = response.data.data;
    if (isDesignElementTestCase(qualityItem)) {
      if (qualityItem.testResult && !qualityItem.testResult.result) {
        // If test result is missing, drop the entire testResult object
        const { testResult, ...rest } = qualityItem;
        qualityItem = rest as DesignElement;
      }
    }

    const parsed = DesignElement.parse(qualityItem);

    if (isDesignElementFMEARisk(parsed)) {
      // The base Risk schema is `.passthrough()`, so FMEA mitigation scores
      // arrive unvalidated/uncoerced (the BE sends them as numbers). Re-parse
      // through FMEARiskDesignElement to coerce them to strings so they match
      // the dropdown options. Fall back to the base-parsed item on failure.
      const narrowed = FMEARiskDesignElement.safeParse(parsed);
      return narrowed.success ? narrowed.data : parsed;
    }

    return parsed;
  },
  async retrieveReviews(company: number, product: string) {
    const response = await getApi().get<{ data: Array<ReviewList> }>(
      `/company/${company}/product-development/product/${product}/change-control`,
    );

    return ReviewList.array().parse(response.data.data);
  },
  async retrieveUsers(company: number) {
    const response = await getApi().get<{ data: Array<QualioUser> }>(
      `/company/${company}/product-development/users`,
    );

    return QualioUser.array().parse(response.data.data);
  },
  async createDesignElement(
    company: number,
    product: string,
    body: Partial<DesignElement>,
  ): Promise<Partial<BaseDesignElement>> {
    switch (body.type) {
      case "req1":
      case "req2":
      case "req3":
      case "req4": {
        const response = await getApi().post<{ data: DesignElement }>(
          `/company/${company}/product-development/product/${product}/requirement`,
          { data: body },
        );

        return BaseDesignElement.partial().parse(response.data.data);
      }
      case "testCase1":
      case "testCase2":
      case "testCase3": {
        const response = await getApi().post<{ data: DesignElement }>(
          `/company/${company}/product-development/product/${product}/test-case`,
          { data: body },
        );

        return BaseDesignElement.partial().parse(response.data.data);
      }
      case "risk": {
        const response = await getApi().post<{ data: DesignElement }>(
          `/company/${company}/product-development/product/${product}/risk`,
          { data: body },
        );

        return BaseDesignElement.partial().parse(response.data.data);
      }
      default:
        throw new Error("Not implemented");
    }
  },
  async modifyDesignElement(
    company: number,
    product: string,
    body: Partial<DesignElement>,
  ) {
    switch (body.type) {
      case "req1":
      case "req2":
      case "req3":
      case "req4": {
        const response = await getApi().patch<{ data: DesignElement }>(
          `/company/${company}/product-development/product/${product}/requirement/${body.id}`,
          { data: body },
        );

        return BaseDesignElement.partial().parse(response.data.data);
      }
      case "testCase1":
      case "testCase2":
      case "testCase3": {
        const response = await getApi().patch<{ data: DesignElement }>(
          `/company/${company}/product-development/product/${product}/test-case/${body.id}`,
          { data: body },
        );

        return BaseDesignElement.partial().parse(response.data.data);
      }
      case "risk": {
        const response = await getApi().patch<{ data: DesignElement }>(
          `/company/${company}/product-development/product/${product}/risk/${body.id}`,
          { data: body },
        );

        return BaseDesignElement.partial().parse(response.data.data);
      }
      default:
        throw new Error("Not implemented");
    }
  },
  async generatePutUrl(
    company: number,
    product: string,
    presignedUrlRequest: Attachment,
  ) {
    const response = await getApi().post<{ data: unknown }>(
      `attachments/company/${company}/product/${product}/generatePresignedUrl`,
      { data: presignedUrlRequest },
    );

    return PresignedUrlResponse.parse(response.data.data);
  },
  async addTestResult(
    company: number,
    product: string,
    testResult: TestResult,
    testCase: string,
  ) {
    return getApi().post(
      `/company/${company}/product-development/product/${product}/test-result`,
      {
        data: {
          ...testResult,
          testCase,
        },
      },
    );
  },
  async retrieveUnstaged(company: number, product: string) {
    const response = await getApi().get(
      `/company/${company}/product-development/product/${product}/change-control/quality-item`,
      {
        params: { status: "unstaged" },
      },
    );

    return LinkedElement.array().parse(response.data.data);
  },
  async createReview(
    company: number,
    product: string,
    review: Partial<ReviewRecord>,
  ) {
    const response = await getApi().post(
      `/company/${company}/product-development/product/${product}/change-control`,
      { data: { ...review, category: "Change Control" } },
    );

    return ReviewRecord.omit({ qualityItems: true }).parse(response.data.data);
  },
  async updateReview(
    company: number,
    product: string,
    reviewId: string,
    review: Partial<ReviewRecord>,
  ) {
    const response = await getApi().put(
      `/company/${company}/product-development/product/${product}/change-control/${reviewId}`,
      { data: { ...review, category: "Change Control" } },
    );

    return ReviewRecord.omit({ qualityItems: true }).parse(response.data.data);
  },
  async retrieveReview(company: number, product: string, reviewId: string) {
    const response = await getApi().get(
      `/company/${company}/product-development/product/${product}/change-control/${reviewId}`,
    );

    return ReviewRecord.parse(response.data.data);
  },
  deleteReview(company: number, product: string, reviewId: string) {
    return getApi()
      .delete(
        `/company/${company}/product-development/product/${product}/change-control/${reviewId}`,
      )
      .then(() => undefined);
  },
  async retrieveRelease(company: number, product: string, releaseId: string) {
    const response = await getApi().get(
      `/company/${company}/product-development/product/${product}/release/${releaseId}`,
    );

    return ReleaseRecord.parse(response.data.data);
  },
  async retrieveGeneratedReports(
    company: number,
    product: string,
    releaseId: string,
  ) {
    const response = await getApi().get(
      `/company/${company}/product-development/product/${product}/release/${releaseId}/document`,
    );

    return ReleaseReport.parse(response.data.data);
  },
  sendForApproval(
    company: number,
    product: string,
    reviewId: string,
  ): Promise<void> {
    return getApi().post(
      `/company/${company}/product-development/product/${product}/change-control/${reviewId}/send-for-approval`,
    );
  },
  revertToDraft(
    company: number,
    product: string,
    reviewId: string,
  ): Promise<void> {
    return getApi().post(
      `/company/${company}/product-development/product/${product}/change-control/${reviewId}/revert-to-draft`,
    );
  },
  approveReview(
    company: number,
    product: string,
    reviewId: string,
    approval: Approval,
  ) {
    return getApi().post(
      `/company/${company}/product-development/product/${product}/change-control/${reviewId}/approve`,
      {
        data: approval,
      },
    );
  },
  async retrieveDesignElementHistory(
    companyId: number,
    productId: string,
    designElementId: string,
  ) {
    const response = await getApi().get(
      `/company/${companyId}/product-development/product/${productId}/qualityItem/${designElementId}/history`,
    );

    return ReviewHistory.array().parse(response.data.data);
  },
  async retrieveDesignElementVersions(
    companyId: number,
    productId: string,
    designElementId: string,
  ) {
    const response = await getApi().get(
      `/company/${companyId}/product-development/product/${productId}/qualityItem/${designElementId}/versions`,
    );

    return DesignElementVersion.array().parse(response.data.data);
  },
  deleteRequirementById(
    companyId: number,
    productId: string,
    designElementId: string,
  ): Promise<void> {
    return getApi()
      .delete(
        `company/${companyId}/product-development/product/${productId}/requirements/${designElementId}`,
      )
      .then((res) => res.data);
  },
  deleteTestCaseById(
    companyId: number,
    productId: string,
    designElementId: string,
  ): Promise<void> {
    return getApi()
      .delete(
        `company/${companyId}/product-development/product/${productId}/test-cases/${designElementId}`,
      )
      .then((res) => res.data);
  },
  deleteRiskById(
    companyId: number,
    productId: string,
    designElementId: string,
  ): Promise<void> {
    return getApi()
      .delete(
        `company/${companyId}/product-development/product/${productId}/risks/${designElementId}`,
      )
      .then((res) => res.data);
  },
  downloadReviewPackageUrl(
    company: number,
    product: string,
    release: string,
  ): string {
    return `${getDesignControlsBaseURL()}/release/company/${company}/product/${product}/release/${release}/download`;
  },

  async fetchPaginatedTraceabilityMatrix(
    url: string,
    limit = 500,
  ): Promise<TraceabilityMatrix> {
    const allData: TraceabilityMatrix = [];
    let cursor: number | undefined = 0;
    let hasNextPage = true;

    while (hasNextPage) {
      const params: Record<string, string | number> = { limit };
      if (cursor !== undefined) params.cursor = cursor;

      const response = await getApi().get(url, { params });
      const { data, pageInfo } = response.data;

      allData.push(...data);
      hasNextPage = pageInfo?.hasNextPage ?? false;
      cursor = pageInfo?.nextCursor ? Number(pageInfo.nextCursor) : undefined;
    }

    return TraceabilityMatrix.parse(allData);
  },

  async traceabilityMatrix(companyId: number, product: string, limit?: number) {
    const url = `/company/${companyId}/product-development/product/${product}/requirement`;
    return this.fetchPaginatedTraceabilityMatrix(url, limit);
  },
  async releaseTraceabilityMatrix(
    companyId: number,
    product: string,
    releaseId: string,
    limit?: number,
  ) {
    const url = `/company/${companyId}/product-development/product/${product}/release/${releaseId}/requirement`;
    return this.fetchPaginatedTraceabilityMatrix(url, limit);
  },
} as const;
